import { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { useThemeApplier } from './hooks/useThemeApplier';
import { useT } from './hooks/useT';
import { useIsMobile } from './hooks/useIsMobile';
import { useBuilder } from './context/BuilderContext';
import { useEditMode } from './context/EditModeContext';
import { useSiteConfigHistory } from './context/SiteConfigContext';
import { useDeleteBlock } from './hooks/useDeleteBlock';
import Sidebar from './components/Sidebar/Sidebar';
import Footer from './components/Footer/Footer';
import PropertyPanel from './components/PropertyPanel/PropertyPanel';
import SidebarSettingsPanel from './components/SidebarSettingsPanel/SidebarSettingsPanel';
import AdminGate from './components/AdminGate/AdminGate';
import Home from './pages/Home/Home';
import Portfolio from './pages/Portfolio/Portfolio';
import Experience from './pages/Experience/Experience';
import AITools from './pages/AITools/AITools';
import Contact from './pages/Contact/Contact';
import PssManPrivacy from './pages/PssManPrivacy/PssManPrivacy';
import styles from './App.module.css';

// The single source of truth for which page key a route shows. Kept here
// because this is also the single source of truth for the routes
// themselves (the <Routes> block below): src/pages/*.js are one-line
// PageRenderer wrappers with no route knowledge of their own, and
// config.sidebar.navItems only carries `path` (admin-editable nav labels,
// not guaranteed to cover every route or stay in sync with it) — deriving
// from either of those would be less reliable than this map, not more.
const ROUTE_PAGE_KEYS = {
  '/': 'home',
  '/portfolio': 'portfolio',
  '/experience': 'experience',
  '/ai-tools': 'aiTools',
  '/contact': 'contact',
};

function NotFound() {
  const t = useT();

  return (
    <div style={{ padding: '3rem 2rem', textAlign: 'center' }}>
      <h1>{t('notfound.title', 'Página no encontrada')}</h1>
    </div>
  );
}

function App() {
  useThemeApplier();
  const { selectedBlockId, activePage, deselectBlock, isDirty, setCurrentPage } = useBuilder();
  const { editMode } = useEditMode();
  const { undo, redo } = useSiteConfigHistory();
  const deleteBlock = useDeleteBlock();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isEditing = editMode !== 'off';

  useEffect(() => {
    deselectBlock();
    setCurrentPage(ROUTE_PAGE_KEYS[location.pathname] || 'home');
  }, [location.pathname, deselectBlock, setCurrentPage]);

  useEffect(() => {
    // Reserves the sheet's `half`-snap height as scroll-padding so that
    // BlockWrapper's `scrollIntoView({ block: 'center' })` (Task 10) lands a
    // newly selected block in the visible band above the sheet, not behind
    // it. Mirrors Sidebar.js's `--sidebar-width` convention: a global style
    // set imperatively on document, cleaned up on the way out.
    if (!isEditing || !isMobile) return undefined;
    document.documentElement.style.setProperty('scroll-padding-bottom', 'calc(var(--fgl-vh) * 50)');
    return () => document.documentElement.style.removeProperty('scroll-padding-bottom');
  }, [isEditing, isMobile]);

  useEffect(() => {
    if (editMode === 'off' || !isDirty) return undefined;

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      // Browsers show their own wording; a non-empty returnValue is the signal.
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [editMode, isDirty]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (editMode === 'off') return;
      if (document.body.dataset.modalOpen) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT' || e.target.isContentEditable) return;

      const mod = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();
      if (mod && key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      // Ctrl+Y is the Windows convention for redo, alongside Ctrl+Shift+Z.
      if (mod && key === 'y') {
        e.preventDefault();
        redo();
        return;
      }

      if (e.key === 'Escape') {
        deselectBlock();
      }

      if (e.key === 'Delete' && selectedBlockId && activePage) {
        deleteBlock(activePage, selectedBlockId);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editMode, selectedBlockId, activePage, deselectBlock, deleteBlock, undo, redo]);

  return (
    <div className={styles.layout}>
      <Sidebar />
      <div className={`${styles.main} ${editMode !== 'off' ? styles.mainEditing : ''}`}>
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/experience" element={<Experience />} />
            <Route path="/ai-tools" element={<AITools />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pss_man/privacy" element={<PssManPrivacy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </div>
      <PropertyPanel />
      <SidebarSettingsPanel />
      <AdminGate />
    </div>
  );
}

export default App;
